import { pgSelect, pgInsert, pgDeleteWhere } from './supabaseClient';
import { logEvent } from './debugLog';

/**
 * 🛍 Retail pack — product finder (feature: 'productFinder'). Products map
 * to the POI (aisle / shelf / department) where they're found; the visitor's
 * destination search matches product names/keywords and offers "Milk →
 * Aisle 4". Managed per-POI in the Dashboard POI form.
 */
export interface Product {
  id: string;
  map_code: string;
  poi_id: string;
  name: string;
  keywords: string | null;
}

let cache: Product[] | null = null;

export async function getProducts(mapCode: string, forceRefresh = false): Promise<Product[]> {
  if (cache && !forceRefresh) return cache;
  cache = await pgSelect<Product>(
    'products',
    `select=*&map_code=eq.${encodeURIComponent(mapCode)}&limit=2000`,
  );
  return cache;
}

/** Visitor search: product name/keyword substring match → poi ids. */
export function searchProducts(products: Product[], query: string): Product[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  return products
    .filter((p) => p.name.toLowerCase().includes(q) || (p.keywords ?? '').toLowerCase().includes(q))
    .slice(0, 8);
}

/** Dashboard: replace a POI's product list ("one per line" textarea). */
export async function setPoiProducts(mapCode: string, poiId: string, lines: string[]): Promise<void> {
  await pgDeleteWhere('products', `map_code=eq.${encodeURIComponent(mapCode)}&poi_id=eq.${encodeURIComponent(poiId)}`);
  for (const line of lines) {
    const [name, keywords] = line.split('|').map((s) => s.trim());
    if (!name) continue;
    await pgInsert('products', { map_code: mapCode, poi_id: poiId, name, keywords: keywords || null });
  }
  cache = null;
  logEvent('admin', `products saved for ${poiId} (${lines.length})`);
}

/** Dashboard: current product lines for the POI form textarea. */
export async function getPoiProductLines(mapCode: string, poiId: string): Promise<string[]> {
  const all = await getProducts(mapCode, true);
  return all
    .filter((p) => p.poi_id === poiId)
    .map((p) => (p.keywords ? `${p.name} | ${p.keywords}` : p.name));
}
